import {
  OPEN_ACTION_STATUSES,
  moveInList,
  type Action,
  type ActionClient,
  type ChangedBy,
  type CreateActionBody,
  type CreateFeedbackBody,
  type R1Detail,
  type R1PrepFields,
  type R1PrepInput,
  type UpdateActionBody,
  type UpdateChecklistItemBody,
} from '@cockpit/shared';

/**
 * Lot AW « Hors connexion » : file des changements INTERNES faits sans réseau (logique pure, testée sans
 * navigateur). Chaque changement garde sa clé d'idempotence (`id`), la version vue par la personne (`base`) et de
 * quoi l'afficher tout de suite (affichage optimiste) ; il est envoyé dans l'ordre au retour du réseau.
 *
 * Rien de ce qui touche l'extérieur n'entre dans cette file (décision de Maxime du 18/10/2026) : la liste des
 * sortes de changements ci-dessous est la liste blanche.
 */

export type OpState = 'pending' | 'failed' | 'conflict';

export interface OpError {
  status: number;
  code: string;
  message: string;
}

/** Champ d'un conflit : ta valeur, la leur (valeurs brutes, mises en mots à l'écran). */
export interface FieldConflict {
  field: string;
  mine: unknown;
  theirs: unknown;
}

interface BaseOp {
  /** uuid créé sur l'appareil = clé d'idempotence. */
  id: string;
  seq: number;
  userId: string;
  createdAt: string;
  state: OpState;
  /** Titre de la carte (ou du R1) au moment du changement, pour la liste des changements. */
  title: string;
  error?: OpError;
}

export type UpdateField = keyof UpdateActionBody;

export interface ActionCreateOp extends BaseOp {
  kind: 'action.create';
  /** Identifiant provisoire (remplacé par celui du serveur après l'envoi). */
  actionId: string;
  body: CreateActionBody;
  snapshot: Action;
}

export interface ActionUpdateOp extends BaseOp {
  kind: 'action.update';
  actionId: string;
  body: UpdateActionBody;
  base: { updatedAt: string | null; fields: Partial<Record<UpdateField, unknown>> };
  /** Client choisi (nom affiché tout de suite). */
  client?: ActionClient | null;
  /** Carte rouverte depuis « Fait cette semaine » : la carte telle qu'elle était. */
  snapshot?: Action;
  conflict?: { current: Action; changedBy: ChangedBy; fields: FieldConflict[] };
}

export interface ActionDoneOp extends BaseOp {
  kind: 'action.done';
  actionId: string;
}

export interface ChecklistAddOp extends BaseOp {
  kind: 'checklist.add';
  actionId: string;
  /** Identifiant provisoire de l'étape. */
  itemId: string;
  label: string;
}

export interface ChecklistUpdateOp extends BaseOp {
  kind: 'checklist.update';
  actionId: string;
  itemId: string;
  body: UpdateChecklistItemBody;
}

export interface ChecklistDeleteOp extends BaseOp {
  kind: 'checklist.delete';
  actionId: string;
  itemId: string;
}

export interface R1PrepOp extends BaseOp {
  kind: 'r1.prep';
  r1Key: string;
  input: R1PrepInput;
  base: { filledAt: string | null; fields: Partial<R1PrepFields> };
  conflict?: { current: R1Detail; changedBy: ChangedBy; fields: FieldConflict[] };
}

export interface FeedbackCreateOp extends BaseOp {
  kind: 'feedback.create';
  body: CreateFeedbackBody;
  /** Photos gardées dans IndexedDB (`blobs`). */
  blobIds: string[];
  /** Retour déjà créé (les photos partent ensuite une à une). */
  feedbackId?: string;
  uploaded: number;
}

export type QueuedOp =
  | ActionCreateOp
  | ActionUpdateOp
  | ActionDoneOp
  | ChecklistAddOp
  | ChecklistUpdateOp
  | ChecklistDeleteOp
  | R1PrepOp
  | FeedbackCreateOp;

export type OpKind = QueuedOp['kind'];

/** La liste blanche : seuls ces changements sont gardés hors connexion. */
export const OFFLINE_OP_KINDS: readonly OpKind[] = [
  'action.create',
  'action.update',
  'action.done',
  'checklist.add',
  'checklist.update',
  'checklist.delete',
  'r1.prep',
  'feedback.create',
];

/** Champs d'une carte « Tâche » modifiables hors connexion (en plus du statut écartée / rouverte). */
export const OFFLINE_UPDATE_FIELDS: readonly UpdateField[] = [
  'title',
  'description',
  'dueDate',
  'assigneeId',
  'clientId',
  'projectId',
  'status',
];

/** « Fait » hors connexion : seulement sur une tâche ajoutée à la main (les cartes de règles relancent les règles). */
export function canDoneOffline(action: Pick<Action, 'type'>): boolean {
  return action.type === 'user_task';
}

/** Ce que vise un changement : deux changements sur la même cible partent dans l'ordre, l'un après l'autre. */
export function targetOf(op: QueuedOp): string {
  switch (op.kind) {
    case 'r1.prep':
      return `r1:${op.r1Key}`;
    case 'feedback.create':
      return `feedback:${op.id}`;
    default:
      return `action:${op.actionId}`;
  }
}

function actionIdOf(op: QueuedOp): string | null {
  return 'actionId' in op ? op.actionId : null;
}

// ------------------------------------------------------------------ Valeurs comparables

export function cleanText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.replace(/\r\n?/g, '\n').trim();
  return text === '' ? null : text;
}

/** Valeur d'un champ modifiable d'une carte, sous la forme envoyée au serveur. */
export function actionField(action: Action, field: UpdateField): unknown {
  switch (field) {
    case 'clientId':
      return action.client?.id ?? null;
    case 'description':
      return cleanText(action.description);
    default:
      return action[field] ?? null;
  }
}

function comparable(field: string, value: unknown): unknown {
  if (field === 'description' || field === 'title') return cleanText(value);
  return value ?? null;
}

/** Version vue par la personne pour les champs que le changement touche. */
export function baseFields(action: Action, body: UpdateActionBody): Partial<Record<UpdateField, unknown>> {
  const fields: Partial<Record<UpdateField, unknown>> = {};
  for (const key of Object.keys(body) as UpdateField[]) {
    if (body[key] !== undefined) fields[key] = actionField(action, key);
  }
  return fields;
}

/**
 * Conflit champ par champ (décision de la maquette) : un champ est en conflit si la version actuelle diffère à la
 * fois de la version vue et de ta valeur. Deux personnes qui changent deux choses différentes : pas de conflit.
 * Le statut (écartée / rouverte) n'est jamais un conflit.
 */
export function fieldConflicts(op: ActionUpdateOp, current: Action): FieldConflict[] {
  const conflicts: FieldConflict[] = [];
  for (const key of Object.keys(op.body) as UpdateField[]) {
    if (key === 'status' || op.body[key] === undefined) continue;
    const mine = comparable(key, op.body[key]);
    const base = comparable(key, op.base.fields[key]);
    const theirs = comparable(key, actionField(current, key));
    if (theirs !== base && theirs !== mine) conflicts.push({ field: key, mine, theirs });
  }
  return conflicts;
}

export function r1FieldConflicts(op: R1PrepOp, current: R1Detail): FieldConflict[] {
  if (!('fields' in op.input)) return [];
  const conflicts: FieldConflict[] = [];
  for (const [key, value] of Object.entries(op.input.fields) as [keyof R1PrepFields, unknown][]) {
    const mine = cleanText(value);
    const base = cleanText(op.base.fields[key]);
    const theirs = cleanText(current.prep?.fields[key] ?? null);
    if (theirs !== base && theirs !== mine) conflicts.push({ field: key, mine, theirs });
  }
  return conflicts;
}

// ------------------------------------------------------------------ File : ajout, ordre, identifiants

/** Prochain numéro d'ordre. */
export function nextSeq(ops: readonly QueuedOp[]): number {
  return ops.reduce((max, op) => Math.max(max, op.seq), 0) + 1;
}

function lastOnTarget(ops: readonly QueuedOp[], target: string): QueuedOp | undefined {
  for (let i = ops.length - 1; i >= 0; i -= 1) {
    const op = ops[i] as QueuedOp;
    if (targetOf(op) === target) return op;
  }
  return undefined;
}

export interface EnqueueResult {
  ops: QueuedOp[];
  /** Changements à écrire (nouveau ou fusionné). */
  saved: QueuedOp[];
  /** Changements devenus inutiles (à effacer). */
  removed: string[];
}

/**
 * Ajoute un changement à la file (triée par `seq`). Deux modifications de suite de la même carte (ou de la même
 * étape) se fusionnent : un texte tapé hors connexion part en une fois. Supprimer une étape ajoutée hors connexion
 * retire simplement son ajout (et ce qui la touchait).
 */
export function enqueue(ops: readonly QueuedOp[], op: QueuedOp): EnqueueResult {
  const list = [...ops].sort((a, b) => a.seq - b.seq);
  const last = lastOnTarget(list, targetOf(op));
  if (last && last.state === 'pending') {
    if (op.kind === 'action.update' && last.kind === 'action.update') {
      const merged: ActionUpdateOp = {
        ...last,
        title: op.title,
        body: { ...last.body, ...op.body },
        base: { updatedAt: last.base.updatedAt, fields: { ...op.base.fields, ...last.base.fields } },
        ...(op.client !== undefined ? { client: op.client } : {}),
        ...(op.snapshot ? { snapshot: op.snapshot } : {}),
      };
      return { ops: list.map((o) => (o.id === last.id ? merged : o)), saved: [merged], removed: [] };
    }
    if (op.kind === 'checklist.update' && last.kind === 'checklist.update' && last.itemId === op.itemId) {
      const merged: ChecklistUpdateOp = { ...last, body: { ...last.body, ...op.body } };
      return { ops: list.map((o) => (o.id === last.id ? merged : o)), saved: [merged], removed: [] };
    }
    if (op.kind === 'r1.prep' && last.kind === 'r1.prep') {
      const input: R1PrepInput =
        'fields' in op.input && 'fields' in last.input
          ? { fields: { ...last.input.fields, ...op.input.fields } }
          : op.input;
      const merged: R1PrepOp = {
        ...last,
        input,
        base: { filledAt: last.base.filledAt, fields: { ...op.base.fields, ...last.base.fields } },
      };
      return { ops: list.map((o) => (o.id === last.id ? merged : o)), saved: [merged], removed: [] };
    }
  }
  if (op.kind === 'checklist.delete') {
    const add = list.find(
      (o) => o.kind === 'checklist.add' && o.itemId === op.itemId && o.state === 'pending',
    );
    if (add) {
      const removed = list
        .filter((o) => o.id === add.id || ('itemId' in o && o.itemId === op.itemId))
        .map((o) => o.id);
      return { ops: list.filter((o) => !removed.includes(o.id)), saved: [], removed };
    }
  }
  return { ops: [...list, op], saved: [op], removed: [] };
}

/**
 * Prochain changement à envoyer : le premier en attente, sauf si un changement plus ancien sur la même cible a
 * échoué ou attend un choix (il bloque les suivants, pour garder l'ordre).
 */
export function nextRunnable(ops: readonly QueuedOp[]): QueuedOp | null {
  const blocked = new Set<string>();
  for (const op of [...ops].sort((a, b) => a.seq - b.seq)) {
    const target = targetOf(op);
    if (op.state !== 'pending') {
      blocked.add(target);
      continue;
    }
    if (!blocked.has(target)) return op;
  }
  return null;
}

/** Remplace les identifiants provisoires (carte ou étape créées hors connexion) par ceux du serveur. */
export function remapIds(ops: readonly QueuedOp[], map: Readonly<Record<string, string>>): QueuedOp[] {
  if (Object.keys(map).length === 0) return [...ops];
  return ops.map((op) => {
    let next = op;
    if ('actionId' in next && map[next.actionId]) next = { ...next, actionId: map[next.actionId] as string };
    if ('itemId' in next && map[next.itemId]) next = { ...next, itemId: map[next.itemId] as string };
    return next;
  });
}

/** Identifiants provisoires → serveur après la création d'une tâche (la carte et ses étapes, dans l'ordre). */
export function createdIdMap(op: ActionCreateOp, created: Action): Record<string, string> {
  const map: Record<string, string> = { [op.actionId]: created.id };
  op.snapshot.checklist.forEach((item, index) => {
    const real = created.checklist[index];
    if (real) map[item.id] = real.id;
  });
  return map;
}

/** Étape ajoutée : la dernière étape de ce libellé dans la carte rendue par le serveur. */
export function addedItemId(op: ChecklistAddOp, action: Action): string | null {
  const label = op.label.trim();
  for (let i = action.checklist.length - 1; i >= 0; i -= 1) {
    const item = action.checklist[i];
    if (item && item.label === label) return item.id;
  }
  return null;
}

// ------------------------------------------------------------------ Affichage optimiste

/** La carte telle qu'elle sera après la modification. */
export function applyUpdate(action: Action, op: Pick<ActionUpdateOp, 'body' | 'client'>): Action {
  const next: Action = { ...action };
  const { body } = op;
  if (body.title !== undefined) next.title = body.title.trim();
  if (body.description !== undefined) next.description = cleanText(body.description);
  if (body.dueDate !== undefined) next.dueDate = body.dueDate;
  if (body.assigneeId !== undefined) next.assigneeId = body.assigneeId;
  if (body.projectId !== undefined) next.projectId = body.projectId;
  if (body.clientId !== undefined) {
    next.client = body.clientId
      ? op.client && op.client.id === body.clientId
        ? op.client
        : action.client?.id === body.clientId
          ? action.client
          : clientStub(body.clientId, '')
      : null;
  }
  if (body.status === 'DISMISSED') next.status = 'DISMISSED';
  if (body.status === 'OPEN') next.status = 'OPEN';
  return next;
}

export function clientStub(id: string, name: string): ActionClient {
  return {
    id,
    name,
    axonautCompanyId: null,
    stripeCustomerId: null,
    evolusiteSiteId: null,
    adminSiteUrl: null,
    superAdminSiteUrl: null,
    quoteFollowupOwnerId: null,
  };
}

export function applyChecklist(
  action: Action,
  op: ChecklistAddOp | ChecklistUpdateOp | ChecklistDeleteOp,
  now: string,
): Action {
  if (op.kind === 'checklist.add') {
    if (action.checklist.some((i) => i.id === op.itemId)) return action;
    return {
      ...action,
      checklist: [
        ...action.checklist,
        { id: op.itemId, label: op.label.trim(), done: false, doneAt: null, doneBy: null },
      ],
    };
  }
  if (op.kind === 'checklist.delete') {
    return { ...action, checklist: action.checklist.filter((i) => i.id !== op.itemId) };
  }
  let checklist = action.checklist.map((item) => {
    if (item.id !== op.itemId) return item;
    const next = { ...item };
    if (op.body.label !== undefined) next.label = op.body.label.trim();
    if (op.body.done !== undefined && op.body.done !== item.done) {
      next.done = op.body.done;
      next.doneAt = op.body.done ? now : null;
      next.doneBy = op.body.done ? op.userId : null;
    }
    return next;
  });
  if (op.body.position !== undefined) checklist = moveInList(checklist, op.itemId, op.body.position);
  return { ...action, checklist };
}

const OPEN = OPEN_ACTION_STATUSES as readonly string[];

/**
 * Cartes ouvertes d'« À faire » avec les changements pas encore envoyés (au chargement depuis le téléphone comme
 * depuis le réseau, tant que la file n'est pas vide).
 */
export function overlayActions(list: readonly Action[], ops: readonly QueuedOp[], now: string): Action[] {
  let result = [...list];
  for (const op of [...ops].sort((a, b) => a.seq - b.seq)) {
    switch (op.kind) {
      case 'action.create':
        if (!result.some((a) => a.id === op.actionId)) result.push(op.snapshot);
        break;
      case 'action.update': {
        if (op.body.status === 'OPEN' && op.snapshot && !result.some((a) => a.id === op.actionId)) {
          result.push({ ...op.snapshot, status: 'OPEN', doneAt: null, doneBy: null });
        }
        result = result
          .map((a) => (a.id === op.actionId ? applyUpdate(a, op) : a))
          .filter((a) => a.id !== op.actionId || OPEN.includes(a.status));
        break;
      }
      case 'action.done':
        result = result.filter((a) => a.id !== op.actionId);
        break;
      case 'checklist.add':
      case 'checklist.update':
      case 'checklist.delete':
        result = result.map((a) => (a.id === op.actionId ? applyChecklist(a, op, now) : a));
        break;
      case 'r1.prep':
        result = result.filter(
          (a) => !(a.type === 'r1_prep' && a.payload.r1Key === op.r1Key && a.assigneeId === op.userId),
        );
        break;
      default:
        break;
    }
  }
  return result;
}

/** « Fait cette semaine » : une carte rouverte hors connexion en sort tout de suite. */
export function overlayDone(list: readonly Action[], ops: readonly QueuedOp[]): Action[] {
  const reopened = new Set(
    ops.filter((o) => o.kind === 'action.update' && o.body.status === 'OPEN').map((o) => actionIdOf(o)),
  );
  return list.filter((a) => !reopened.has(a.id));
}

/** Volet d'un R1 avec les infos complétées hors connexion. */
export function overlayR1(detail: R1Detail, ops: readonly QueuedOp[]): R1Detail {
  let next = detail;
  for (const op of ops) {
    if (op.kind !== 'r1.prep' || op.r1Key !== detail.r1Key) continue;
    const prep = next.prep ?? {
      r1Key: detail.r1Key,
      fields: {
        company: null,
        website: null,
        channel: null,
        referrer: null,
        contactName: null,
        contactRole: null,
        need: null,
        notes: null,
      },
      prefilledKeys: [],
      nothingToAdd: false,
      filledBy: null,
      filledAt: null,
    };
    const fields = { ...prep.fields };
    if ('fields' in op.input) {
      for (const [key, value] of Object.entries(op.input.fields) as [keyof R1PrepFields, unknown][]) {
        fields[key] = cleanText(value);
      }
    }
    next = {
      ...next,
      prep: {
        ...prep,
        fields,
        nothingToAdd: 'nothingToAdd' in op.input,
        filledBy: op.userId,
        filledAt: prep.filledAt,
      },
    };
  }
  return next;
}

/** Cartes et étapes qui ont un changement pas encore envoyé (pastille « en attente d'envoi »). */
export function pendingTargets(ops: readonly QueuedOp[]): { actions: Set<string>; items: Set<string> } {
  const actions = new Set<string>();
  const items = new Set<string>();
  for (const op of ops) {
    const id = actionIdOf(op);
    if (id) actions.add(id);
    if ('itemId' in op) items.add(op.itemId);
  }
  return { actions, items };
}

// ------------------------------------------------------------------ Libellés

function short(text: string, max = 48): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

/** Libellé d'un changement dans la liste « changements non envoyés ». */
export function opLabel(op: QueuedOp): string {
  const title = `« ${short(op.title)} »`;
  switch (op.kind) {
    case 'action.create':
      return `Nouvelle tâche ${title}`;
    case 'action.update':
      if (op.body.status === 'DISMISSED') return `Carte écartée ${title}`;
      if (op.body.status === 'OPEN') return `Carte remise dans la liste ${title}`;
      return `Carte modifiée ${title}`;
    case 'action.done':
      return `« Fait » sur ${title}`;
    case 'checklist.add':
      return `Étape ajoutée « ${short(op.label, 32)} » · ${title}`;
    case 'checklist.update':
      if (op.body.done !== undefined) return `Étape ${op.body.done ? 'cochée' : 'décochée'} · ${title}`;
      if (op.body.label !== undefined) return `Étape renommée · ${title}`;
      return `Étape déplacée · ${title}`;
    case 'checklist.delete':
      return `Étape supprimée · ${title}`;
    case 'r1.prep':
      return `Infos du R1 · ${title}`;
    case 'feedback.create':
      return `Retour ${title}`;
    default:
      return title;
  }
}

/** « 3 changements en attente » / « 1 changement en attente ». */
export function pendingText(count: number, long = true): string {
  if (!long) return `${count} en attente`;
  return `${count} changement${count > 1 ? 's' : ''} en attente`;
}

/** Toast du retour du réseau : « 3 changements envoyés · 1 à trancher ». */
export function sentText(sent: number, conflicts: number, failed: number): string | null {
  const parts: string[] = [];
  if (sent > 0) parts.push(`${sent} changement${sent > 1 ? 's' : ''} envoyé${sent > 1 ? 's' : ''}`);
  if (conflicts > 0) parts.push(`${conflicts} à trancher`);
  if (failed > 0) parts.push(`${failed} non envoyé${failed > 1 ? 's' : ''}`);
  return parts.length > 0 ? parts.join(' · ') : null;
}
